import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Container from "../../templates/Container/Container";
import Wizard from "../../organisms/Wizard/Wizard";
import SpinnerLoader from "../../atoms/SpinnerLoader/SpinnerLoader";
import Warning from "../../atoms/Warning/Warning";
import { useAppDispatch, useAppSelector } from "../../../rtk/_config/hooks";
import { setTheme } from "../../../rtk/theme/theme";
import { RootState } from "../../../types/rtk/api-config/types";
import { useGetTokenMutation } from "../../../rtk/auth/login";
import { setAuth } from "../../../rtk/auth/auth";
import { useGetMyUserQuery } from "../../../rtk/user/user";
import { useLazyGetAnimalQuery, useGetBreedsQuery } from "../../../rtk/animal/animal";
import { AppRoutes } from "../../../routes/AppRoutes";
import { useNavigateWithQuery } from '../../../helpers/useNavigationWithQuery'

const MainPage = () => {
  const [searchParams] = useSearchParams();
  const navigateWithQuery = useNavigateWithQuery();
  const location = useLocation();
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();

  // URL params
  const mailToken = searchParams.get("mail_token");
  const trackerId = searchParams.get("trackerId");
  const dark = searchParams.get("dark");
  const lang = searchParams.get("lang");

  // Store state
  const token = useAppSelector((state: RootState) => state.auth.token);
  const mailTokenStore = useAppSelector((state: RootState) => state.auth.mail_token);

  // Local state
  const [timeLine, setTimeLine] = useState(
    location.state && location.state.timeLine !== 0
      ? location.state.timeLine
      : 0
  );
  const [authError, setAuthError] = useState<string | null>(null);

  // API hooks
  const [getToken] = useGetTokenMutation();
  const [getAnimal, { data: animalData, isFetching: isLoadingAnimal, isSuccess: hasAnimalData }] = useLazyGetAnimalQuery();

  // Authenticate on app load when mail_token is present
  useEffect(() => {
    const authenticate = async () => {
      // Skip if no mail_token or trackerId in URL
      if (!mailToken || !trackerId) return;

      // Skip if mail_token hasn't changed
      if (mailToken === mailTokenStore) return;

      try {
        const response = await getToken({ mail_token: mailToken }).unwrap();
        dispatch(setAuth({
          token: response.access_token,
          mail_token: mailToken,
        }));
        setAuthError(null);
      } catch (error) {
        console.error("Authentication error:", error);
        setAuthError("Authentication failed. Please check your link or contact support.");
      }
    };

    authenticate();
  }, [mailToken, mailTokenStore, trackerId, getToken, dispatch]);

  // Fetch breeds with RTK Query (automatic caching and refetching)
  const { isLoading: isLoadingBreeds } = useGetBreedsQuery(
    { language: i18n.language as any },
    { skip: !token }
  );

  // Fetch animal data synchronously when the token is available
  useEffect(() => {
    if (token && trackerId) {
      getAnimal(trackerId);
    }
  }, [token, trackerId, getAnimal]);

  // Fetch and set form lang: prioritize URL param, fallback to user data
  const { data: myUser, isLoading: isLoadingMyUser } = useGetMyUserQuery("", {
    skip: !token || !!lang,
  });
  
  useEffect(() => {
    const targetLang = lang || myUser?.language;
    if (targetLang && targetLang !== i18n.language) {
      i18n.changeLanguage(targetLang);
    }
  }, [lang, myUser, i18n]);

  // Set theme according to URL param
  useEffect(() => {
    if (dark) {
      dispatch(setTheme({ mode: dark === "0" ? "light" : "dark" }));
    }
  }, [dark, dispatch]);

  // Redirect after animal fetch completes with API data (profile already exists)
  useEffect(() => {
    if (!isLoadingAnimal && hasAnimalData && animalData?.items[0]) {
      navigateWithQuery(AppRoutes.INFOS_VALIDATIONS, {
        state: {
          formData: animalData?.items[0],
          animalId: animalData?.items[0].id,
          noArrow: true,
        },
      });
    }
  }, [isLoadingAnimal, hasAnimalData, animalData, navigateWithQuery]);

  return (
    <Container>
      {authError ? (
        <Warning>{authError}</Warning>
      ) : isLoadingBreeds || isLoadingMyUser || isLoadingAnimal ? (
        <SpinnerLoader />
      ) : (
        <Wizard
          timeLine={timeLine}
          setTimeLine={setTimeLine}
          formDataFromLocation={location.state && location.state.formData}
        />
      )}
    </Container>
  );
};

export default MainPage;